import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Tag, Calendar, Package, Dumbbell, Users, MapPin, Star, HelpCircle, Newspaper, LayoutPanelTop, Link2, Volleyball, FileText, Settings, LifeBuoy, Inbox, LogOut, Menu, ExternalLink, BarChart3, Search, MessageSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { canEditType, canManageSupport, canManageSettings, ROLE_LABEL } from "@/config/contentSchemas";

// content nav entries map to /admin/:type
const CONTENT_NAV = [
  { type: "page_sections", label: "Page Sections", icon: LayoutPanelTop },
  { type: "offers", label: "Offers", icon: Tag },
  { type: "events", label: "Events", icon: Calendar },
  { type: "packages", label: "Packages", icon: Package },
  { type: "services", label: "Services", icon: Dumbbell },
  { type: "coaches", label: "Coaches", icon: Users },
  { type: "locations", label: "Locations", icon: MapPin },
  { type: "testimonials", label: "Testimonials", icon: Star },
  { type: "faqs", label: "FAQs", icon: HelpCircle },
  { type: "posts", label: "News & Posts", icon: Newspaper },
  { type: "booking_links", label: "Booking Links", icon: Link2 },
  { type: "volleyball_resources", label: "Volleyball", icon: Volleyball },
  { type: "policies", label: "Legal / Policies", icon: FileText },
];

// Sidebar is organized into labeled groups; all pages & sections live in one "Pages & Content" group.
const buildNavGroups = (role) => {
  const groups = [];

  // Overview (no header)
  groups.push({ label: null, items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }] });

  // Pages & Content — every page/section editor in one place
  const content = [];
  if (role === "admin" || role === "editor") content.push({ to: "/admin/pages", label: "Pages", icon: LayoutPanelTop });
  CONTENT_NAV.forEach((c) => {
    if (canEditType(role, c.type)) content.push({ to: `/admin/${c.type}`, label: c.label, icon: c.icon });
  });
  if (content.length) groups.push({ label: "Pages & Content", items: content });

  // Insights
  const insights = [{ to: "/admin/analytics", label: "Analytics", icon: BarChart3 }];
  if (role === "admin" || role === "editor") insights.push({ to: "/admin/seo", label: "SEO Manager", icon: Search });
  groups.push({ label: "Insights", items: insights });

  // Inbox
  if (canManageSupport(role)) {
    groups.push({ label: "Inbox", items: [
      { to: "/admin/leads", label: "Leads", icon: Inbox },
      { to: "/admin/tickets", label: "Support Tickets", icon: LifeBuoy },
    ] });
  }

  // System
  if (canManageSettings(role)) {
    groups.push({ label: "System", items: [
      { to: "/admin/chat", label: "AI Live Chat", icon: MessageSquare },
      { to: "/admin/settings", label: "Settings & Integrations", icon: Settings },
    ] });
  }

  return groups;
};

const NavLinkItem = ({ n, onNavigate }) => {
  const Icon = n.icon;
  return (
    <NavLink to={n.to} end={n.end} onClick={onNavigate}
      className={({ isActive }) => cn(
        "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
        isActive ? "bg-brand text-white" : "text-foreground/80 hover:bg-accent"
      )}>
      <Icon className="h-4 w-4" /> {n.label}
    </NavLink>
  );
};

const NavList = ({ role, onNavigate }) => {
  const groups = buildNavGroups(role);
  const plain = groups.filter((g) => !g.label);
  const labeled = groups.filter((g) => g.label);
  const firstLabel = labeled.length ? labeled[0].label : null;
  return (
    <nav className="flex flex-col gap-2 p-3" data-testid="admin-nav">
      {plain.map((group) => (
        <div key={group.items[0]?.to || "plain"} className="flex flex-col gap-1">
          {group.items.map((n) => <NavLinkItem key={n.to} n={n} onNavigate={onNavigate} />)}
        </div>
      ))}
      <Accordion type="multiple" defaultValue={firstLabel ? [firstLabel] : []} className="flex flex-col">
        {labeled.map((group) => (
          <AccordionItem key={group.label} value={group.label} className="border-none" data-testid={`admin-navgroup-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <AccordionTrigger className="rounded-md px-3 py-2 font-condensed text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:bg-accent hover:no-underline">
              {group.label}
            </AccordionTrigger>
            <AccordionContent className="pb-1 pt-0.5">
              <div className="flex flex-col gap-1">
                {group.items.map((n) => <NavLinkItem key={n.to} n={n} onNavigate={onNavigate} />)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </nav>
  );
};

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-muted/20">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-card lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-border px-4">
          <img src={settings.logo_url} alt="Trilogy" className="h-8 w-auto" />
          <span className="font-condensed text-sm font-semibold uppercase tracking-wide">Admin</span>
        </div>
        <NavList role={role} />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card px-4">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="flex h-10 w-10 items-center justify-center rounded-md border border-border lg:hidden" data-testid="admin-menu-button"><Menu className="h-5 w-5" /></button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 overflow-y-auto p-0">
                <div className="flex h-16 items-center gap-2 border-b border-border px-4"><img src={settings.logo_url} alt="Trilogy" className="h-8 w-auto" /><span className="font-condensed text-sm font-semibold uppercase">Admin</span></div>
                <NavList role={role} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="hidden sm:block">
              <span className="font-condensed text-sm uppercase tracking-wide text-muted-foreground">{user?.email}</span>
              <span className="ml-2 rounded-full bg-brand/15 px-2 py-0.5 text-[11px] font-semibold uppercase text-brand">{ROLE_LABEL[role] || role}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><ExternalLink className="h-3.5 w-3.5" /> View Site</Link>
            <button onClick={doLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent transition-colors"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
